import React, { useState, useEffect } from 'react';
import apiService from '../services/api';
import '../styles/SignupForm.css';

const SignupForm = ({ onSignupSuccess }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    state: '',
    reason: '',
    documents: null,
    password: '',
    affiliatePartnerType: '',
    organizationType: '',
    teamOrganizationName: '',
    wasReferred: false, 
    referredBy: '' 
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [states, setStates] = useState([]);
  const [statesLoading, setStatesLoading] = useState(false);

  // Partner type options
  const partnerTypes = [
    { value: 'individual', label: 'Individual' },
    { value: 'team', label: 'Team (More than 1 person)' },
    { value: 'student_organization', label: 'Student Organization' },
    { value: 'business', label: 'Business' }
  ];

  // Organization type options
  const organizationTypes = [
    { value: 'academic', label: 'Academic' },
    { value: 'career', label: 'Career' },
    { value: 'cultural', label: 'Cultural' },
    { value: 'greek_letter', label: 'Greek Letter' },
    { value: 'service_volunteer', label: 'Service/Volunteer' },
    { value: 'sports', label: 'Sports' },
    { value: 'other', label: 'Other' }
  ];

  // Fetch states from API
  useEffect(() => {
    const fetchStates = async () => {
      const headers = {
        'devicemodel': 'web-browser',
        'deviceuniqueid': 'web-app-12345',
        'Content-Type': 'application/json'
      };

      setStatesLoading(true);
      try {
        const statesResponse = await fetch('https://api.u-single2.com/api/discover/states', {
          method: 'GET',
          headers
        });

        if (statesResponse.ok) {
          const statesData = await statesResponse.json();
          if (statesData.success && statesData.states && Array.isArray(statesData.states)) {
            setStates(statesData.states);
          }
        }
      } catch (err) {
        console.error('Error fetching states:', err);
      } finally {
        setStatesLoading(false);
      }
    };

    fetchStates();
  }, []);

  const validateReferralCode = (code) => {
    if (!code) return false;
    if (code.length !== 8) return false;
    if (!code.startsWith('KK0')) return false;
    return true;
  };

  const handleChange = (e) => {
    if (e.target.type === "file") {
      setFormData({
        ...formData,
        [e.target.name]: e.target.files[0],
      });
    } else if (e.target.type === "checkbox") {
      // Handle checkbox inputs
      const newFormData = {
        ...formData,
        [e.target.name]: e.target.checked,
      };

      // If unchecking "wasReferred", also clear the referral code
      if (e.target.name === "wasReferred" && !e.target.checked) {
        newFormData.referredBy = "";
      }

      setFormData(newFormData);
    } else {
      setFormData({
        ...formData,
        [e.target.name]: e.target.value,
      });
    }

    // Handle affiliate partner type changes
    if (e.target.name === "affiliatePartnerType") {
      const newFormData = {
        ...formData,
        [e.target.name]: e.target.value,
        // Reset dependent fields when partner type changes
        organizationType: "",
        teamOrganizationName: "",
      };
      setFormData(newFormData);
    }

    // Clear error when user starts typing
    if (error) setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // Validate required fields based on partner type
      if (formData.affiliatePartnerType === 'team' || formData.affiliatePartnerType === 'student_organization') {
        if (!formData.teamOrganizationName.trim()) {
          setError('Team/Organization Name is required for this partner type');
          setLoading(false);
          return;
        }
      }

      if (formData.affiliatePartnerType === 'student_organization' && !formData.organizationType) {
        setError('Organization Type is required for Student Organization');
        setLoading(false);
        return;
      }

      if (formData.wasReferred) {        
        if (!validateReferralCode(formData.referredBy)) {
          setError('Referral Code must be exactly 8 characters and start with "KK0"');
          setLoading(false);
          return;
        }
      }

      // Create FormData object for file upload
      const submitData = new FormData();
      
      // Append all form fields
      Object.keys(formData).forEach(key => {
        if (formData[key] !== null && formData[key] !== '') {
          submitData.append(key, formData[key]);
        }
      });

      const result = await apiService.auth.signup(submitData);
      
      if (result.success) {
        setSuccess(result.message);
        
        // Call success callback if provided
        if (onSignupSuccess) {
          onSignupSuccess(result.data.user);
        }
        
        // Reset form
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          state: '',
          reason: '',
          documents: null,
          password: '',
          affiliatePartnerType: '',
          organizationType: '',
          teamOrganizationName: '',
          wasReferred: false, 
          referredBy: '' 
        });
        
        // Reset file input
        const fileInput = document.getElementById('documents');
        if (fileInput) fileInput.value = '';
        
      }
    } catch (err) {
      setError(err.message || 'An error occurred during signup');
      console.error('Signup error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form className="signup-form" onSubmit={handleSubmit}>
      <div className="signup-note">
        <p>Please fill out the application form below to join our affiliate program. All fields marked with * are required.</p>
      </div>

      {error && (
        <div className="error-message">
          <span className="error-icon">⚠️</span>
          {error}
        </div>
      )}

      {success && (
        <div className="success-message">
          <span className="success-icon">✅</span>
          {success}
        </div>
      )}

      <div className="form-group">
        <label htmlFor="firstName">First Name *</label>
        <input
          type="text"
          id="firstName"
          name="firstName"
          value={formData.firstName}
          onChange={handleChange}
          required
          placeholder="Enter your first name"
          disabled={loading}
        />
      </div>

      <div className="form-group">
        <label htmlFor="lastName">Last Name </label>
        <input
          type="text"
          id="lastName"
          name="lastName"
          value={formData.lastName}
          onChange={handleChange}
          required
          placeholder="Enter your last name"
          disabled={loading}
        />
      </div>

      <div className="form-group">
        <label htmlFor="email">Email Address *</label>
        <input
          type="email"
          id="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          required
          placeholder="Enter your email address"
          disabled={loading}
        />
      </div>

      <div className="form-group">
        <label htmlFor="phone">Phone Number *</label>
        <input
          type="tel"
          id="phone"
          name="phone"
          value={formData.phone}
          onChange={handleChange}
          required
          placeholder="Enter your phone number"
          disabled={loading}
        />
      </div>

      <div className="form-group">
        <label htmlFor="affiliatePartnerType">Affiliate Partner Type *</label>
        <select
          id="affiliatePartnerType"
          name="affiliatePartnerType"
          value={formData.affiliatePartnerType}
          onChange={handleChange}
          required
          disabled={loading}
        >
          <option value="">Select your partner type</option>
          {partnerTypes.map((type) => (
            <option key={type.value} value={type.value}>
              {type.label}
            </option>
          ))}
        </select>
      </div>

      {/* Conditional Organization Type Field */}
      {formData.affiliatePartnerType === 'student_organization' && (
        <div className="form-group">
          <label htmlFor="organizationType">Organization Type *</label>
          <select
            id="organizationType"
            name="organizationType"
            value={formData.organizationType}
            onChange={handleChange}
            required
            disabled={loading}
          >
            <option value="">Select organization type</option>
            {organizationTypes.map((type) => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Conditional Team/Organization Name Field */}
      {(formData.affiliatePartnerType === 'team' || formData.affiliatePartnerType === 'student_organization' || formData.affiliatePartnerType === 'business') && (
        <div className="form-group">
          <label htmlFor="teamOrganizationName">
            {formData.affiliatePartnerType === 'team' ? 'Team Name *' : formData.affiliatePartnerType === 'business' ? 'Business Name *' : 'Organization Name *'}
          </label>
          <input
            type="text"
            id="teamOrganizationName"
            name="teamOrganizationName"
            value={formData.teamOrganizationName}
            onChange={handleChange}
            required
            placeholder={formData.affiliatePartnerType === 'team' ? 'Enter your team name' : formData.affiliatePartnerType === 'business' ? 'Enter your business name' : 'Enter your organization name'}
            disabled={loading}
          />
        </div>
      )}

      <div className="form-group">
        <label htmlFor="state">State *</label>
        <select
          id="state"
          name="state"
          value={formData.state}
          onChange={handleChange}
          required
          disabled={loading || statesLoading}
        >
          <option value="">
            {statesLoading ? 'Loading states...' : 'Select your state'}
          </option>
          {states.map((state) => (
            <option key={state.id} value={state.state}>
              {state.state}
            </option>
          ))}
        </select>
        {statesLoading && (
          <small className="loading-info">Loading states from server...</small>
        )}
      </div>

      <div className="form-group">
        <label htmlFor="reason">Reason for Joining *</label>
        <textarea
          id="reason"
          name="reason"
          value={formData.reason}
          onChange={handleChange}
          required
          placeholder="Tell us why you want to join our affiliate program"
          rows="4"
          disabled={loading}
        />
      </div>

      <div className="form-group">
        <label htmlFor="documents">Supporting Documents (Optional)</label>
        <input
          type="file"
          id="documents"
          name="documents"
          onChange={handleChange}
          accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
          disabled={loading}
        />
        <small className="file-info">
          Accepted formats: PDF, DOC, DOCX, JPG, JPEG, PNG (Max 5MB)
        </small>
      </div>

      <div className="form-group">
        <label htmlFor="password">Password *</label>
        <input
          type="password"
          id="password"
          name="password"
          value={formData.password}
          onChange={handleChange}
          required
          placeholder="Create a strong password"
          disabled={loading}
        />
        <small className="password-info">
          Password must contain at least 8 characters with uppercase, lowercase, number, and special character
        </small>
      </div>

      <div className="form-group" style={{ marginLeft: '1%' }}>
        <label className="checkbox-label">
          <input
            type="checkbox"
            name="wasReferred"
            checked={formData.wasReferred || false}
            onChange={handleChange}
            disabled={loading}
          />
          <span className="checkbox-text">Were you referred?</span>
        </label>
      </div>

      {formData.wasReferred && (
        <div className="form-group">
          <label htmlFor="referredBy">Referral Code *</label>
          <input
            type="text"
            id="referredBy"
            name="referredBy"
            value={formData.referredBy || ''}
            onChange={handleChange}
            required
            placeholder="Enter your referral code"
            disabled={loading}
          />
        </div>
      )}

      <button type="submit" className="submit-btn" disabled={loading}>
        {loading ? (
          <>
            <span className="loading-spinner"></span>
            Submitting Application...
          </>
        ) : (
          'Submit Application'
        )}
      </button>
    </form>
  );
};

export default SignupForm;